/**
 * Vue Composable for Permission Checking
 *
 * Provides reactive permission checking in Vue components
 * Automatically uses the current brand and rep from stores
 *
 * Usage:
 *   import { usePermissions } from '@/composables/usePermissions'
 *
 *   const { can, filter } = usePermissions()
 *
 *   // In template or script
 *   if (can('viewEmail')) {
 *     // Show email
 *   }
 *
 *   // Filter data
 *   const filteredStaff = filter(allStaff, 'viewStaff')
 */

import {computed} from 'vue'
import {useWeekStore} from '@/stores/week'
import {permissionService} from '@/services/PermissionService'

/**
 * Get the current brand from build-time constant
 * @returns {string} - Current brand name
 */
function getCurrentBrand() {
    // __BRAND__ is defined at build time by Vite's define option in vite.config.js
    const brand = typeof __BRAND__ !== 'undefined' ? __BRAND__ : null
    // When using the default brand (npm run dev without BRAND env),
    // fall back to the dictionary's brand property which reflects the intended brand
    if (!brand || brand === 'default') {
        return typeof __DICT__ !== 'undefined' ? (__DICT__.brand || 'snowfuze') : 'snowfuze'
    }
    return brand
}

/**
 * Permission checking composable
 * @returns {Object} - Permission checking methods
 */
export function usePermissions() {
    const weekStore = useWeekStore()

    /**
     * Get current context for permission checks
     */
    const context = computed(() => ({
        rep: weekStore.repObj,
        brand: getCurrentBrand()
    }))

    /**
     * Check if current user can perform an action
     * @param {string} action - Permission action name
     * @param {Object} additionalContext - Optional additional context to merge with defaults
     * @returns {boolean} - Whether permission is granted
     */
    function can(action, additionalContext = null) {
        const ctx = additionalContext
            ? { ...context.value, ...additionalContext }
            : context.value
        return permissionService.can(action, ctx)
    }

    /**
     * Filter data based on permission rules
     * @param {Array} data - Data to filter
     * @param {string} action - Permission action name
     * @param {Object} additionalContext - Optional additional context to merge with defaults
     * @returns {Array} - Filtered data
     */
    function filter(data, action, additionalContext = null) {
        const ctx = additionalContext
            ? { ...context.value, ...additionalContext }
            : context.value
        return permissionService.filter(data, action, ctx)
    }

    /**
     * Check multiple permissions at once
     * @param {Array<string>} actions - Array of permission action names
     * @param {Object} additionalContext - Optional additional context to merge with defaults
     * @returns {Object} - Object with action names as keys and boolean results
     */
    function canMultiple(actions, additionalContext = null) {
        const ctx = additionalContext
            ? { ...context.value, ...additionalContext }
            : context.value
        return permissionService.canMultiple(actions, ctx)
    }

    /**
     * Computed permissions for common use cases
     * These can be used directly in templates
     */
    const permissions = computed(() => ({
        canViewEmail: can('viewEmail'),
        canViewSensitiveData: can('viewSensitiveData'),
        canViewAllLiftPasses: can('viewAllLiftPasses'),
        canViewAllAccommodation: can('viewAllAccommodation'),
        canViewAllStaff: can('viewAllStaff'),
        canViewCrossBrandStaff: can('viewCrossBrandStaff'),
        canViewAllPassengers: can('viewAllPassengers'),
        canViewTransportPassenger: can('viewTransportPassenger')
    }))

    return {
        can,
        filter,
        canMultiple,
        permissions,
        context
    }
}
